import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, X, Download, Play, Image as ImageIcon } from 'lucide-react';
import PageLoader from '../components/PageLoader';
import LoadMoreButton from '../components/LoadMoreButton';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import { usePaginatedFetch } from '../hooks/usePaginatedFetch';
import { linkify } from '../utils/linkify';
import '../styles/content-vault.css';

interface ContentItem {
  submissionId: string;
  challengeId: string;
  challengeTitle: string;
  challengeDescription?: string | null;
  userId: string;
  employeeName: string;
  contributors: string[];
  mediaUrls: string[];
  submittedAt: string;
  reviewedAt: string;
  zipKey?: string | null;
}

export default function ContentVaultPage() {
  const { token } = useAuth();
  const [selectedContent, setSelectedContent] = useState<ContentItem | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const {
    items: content,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
  } = usePaginatedFetch<ContentItem>({
    url: `${authConfig.apiUrl}/api/admin/content-vault`,
    token,
    dataKey: 'submissions',
    limit: 20,
  });

  const isVideo = (url: string) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.m4v'];
    return videoExtensions.some(ext => url.toLowerCase().includes(ext));
  };

  const getFileExtension = (url: string) => {
    if (!url) return 'mp4';
    const match = url.match(/\.(mp4|mov|avi|webm|m4v|jpg|jpeg|png|gif)(\?|$)/i);
    return match ? match[1].toLowerCase() : 'mp4';
  };

  const handleDownload = async (item: ContentItem, url: string, assetIndex: number, totalAssets: number) => {
    try {
      if (!token) throw new Error('Not authenticated');
      if (!url) throw new Error('Media URL is missing from item');

      const momentName = item.challengeTitle.replace(/[^a-zA-Z0-9]/g, '_');
      const employeeName = item.employeeName.replace(/[^a-zA-Z0-9]/g, '_');
      const fileExtension = getFileExtension(url);
      const assetSuffix = totalAssets > 1 ? `_${assetIndex + 1}` : '';
      const fileName = `${momentName}_${employeeName}${assetSuffix}.${fileExtension}`;

      const response = await fetch(`${authConfig.apiUrl}/api/admin/content-vault/download-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ mediaUrl: url, fileName }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to generate download URL');
      }

      const data = await response.json();
      const a = document.createElement('a');
      a.href = data.downloadUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download media');
    }
  };

  const handleDownloadZip = async (item: ContentItem) => {
    try {
      if (!token) throw new Error('Not authenticated');

      const momentName = item.challengeTitle.replace(/[^a-zA-Z0-9]/g, '_');
      const employeeName = item.employeeName.replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `${momentName}_${employeeName}_all_assets.zip`;

      const response = await fetch(`${authConfig.apiUrl}/api/admin/content-vault/submission-zip-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ submissionId: item.submissionId, fileName }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to generate zip URL');
      }

      const data = await response.json();
      const a = document.createElement('a');
      a.href = data.downloadUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Zip download failed:', err);
      alert('Failed to download zip. Please download files individually.');
    }
  };

  const openModal = (item: ContentItem) => {
    setSelectedContent(item);
    setLightboxIndex(0);
  };

  const closeModal = () => {
    setSelectedContent(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="content-vault-container">
        <PageLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-vault-container">
        <div className="vault-error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="content-vault-container">
      <div className="vault-header">
        <div>
          <h1 className="vault-title">Content Vault</h1>
          <p className="vault-subtitle">Brand-owned assets ready for marketing</p>
        </div>
      </div>

      {content.length === 0 ? (
        <div className="vault-empty">
          <p>No approved content yet.</p>
          <p className="empty-subtitle">Approved submissions will appear here.</p>
        </div>
      ) : (
        <div className="vault-grid">
          {content.map((item) => (
            <div key={item.submissionId} className="vault-card" onClick={() => openModal(item)}>
              <div className="vault-thumbnail">
                {isVideo(item.mediaUrls[0]) ? (
                  <video src={`${item.mediaUrls[0]}#t=0.001`} className="thumbnail-video" muted playsInline preload="metadata" />
                ) : (
                  <img src={item.mediaUrls[0]} alt={item.challengeTitle} className="thumbnail-image" />
                )}
                <div className="play-overlay">
                  <div className="play-icon">{isVideo(item.mediaUrls[0]) ? <Play size={24} color="#1f2937" style={{ marginLeft: '2px' }} /> : <ImageIcon size={24} color="#1f2937" />}</div>
                </div>
                {item.mediaUrls.length > 1 && (
                  <span className="vault-asset-count-badge">+{item.mediaUrls.length - 1} more</span>
                )}
              </div>
              <div className="vault-card-footer">
                <div className="card-meta">
                  <div className="card-meta-label">Moment</div>
                  <div className="card-moment">{item.challengeTitle}</div>
                  <div className="card-meta-label" style={{ marginTop: '8px' }}>
                    {item.contributors && item.contributors.length > 1 ? 'Contributors' : 'Author'}
                  </div>
                  <div className="card-employee">
                    {item.contributors && item.contributors.length > 0
                      ? item.contributors.join(', ')
                      : item.employeeName}
                  </div>
                  <div className="card-date">{formatDate(item.reviewedAt)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <LoadMoreButton onClick={loadMore} loading={loadingMore} hasMore={hasMore} />

      {/* Media Modal */}
      {selectedContent && (
        <div className="vault-modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <X size={18} />
            </button>

            <div className="modal-video-container" style={{ position: 'relative' }}>
              {isVideo(selectedContent.mediaUrls[lightboxIndex]) ? (
                <video
                  key={lightboxIndex}
                  src={selectedContent.mediaUrls[lightboxIndex]}
                  controls
                  autoPlay
                  muted
                  playsInline
                  className="modal-video"
                />
              ) : (
                <img
                  src={selectedContent.mediaUrls[lightboxIndex]}
                  alt={selectedContent.challengeTitle}
                  className="modal-image"
                />
              )}
              {selectedContent.mediaUrls.length > 1 && (
                <>
                  <button
                    className="vault-modal-nav vault-modal-nav--prev"
                    onClick={() => setLightboxIndex((i) => i - 1)}
                    disabled={lightboxIndex === 0}
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    className="vault-modal-nav vault-modal-nav--next"
                    onClick={() => setLightboxIndex((i) => i + 1)}
                    disabled={lightboxIndex === selectedContent.mediaUrls.length - 1}
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}
            </div>

            <div className="modal-footer">
              <div className="modal-meta">
                <h3 className="modal-moment">{selectedContent.challengeTitle}</h3>
                {selectedContent.challengeDescription && (
                  <p className="modal-challenge-description">{linkify(selectedContent.challengeDescription)}</p>
                )}
                <p className="modal-meta-label">
                  {selectedContent.contributors && selectedContent.contributors.length > 1 ? 'Contributors' : 'Author'}
                </p>
                <p className="modal-employee">
                  {selectedContent.contributors && selectedContent.contributors.length > 0
                    ? selectedContent.contributors.join(', ')
                    : selectedContent.employeeName}
                </p>
                <p className="modal-date">{formatDate(selectedContent.reviewedAt)}</p>
              </div>

              {selectedContent.mediaUrls.length > 1 && (
                <div className="vault-filmstrip">
                  {selectedContent.mediaUrls.map((url, i) => (
                    <div
                      key={i}
                      className={`vault-filmstrip-thumb${i === lightboxIndex ? ' vault-filmstrip-thumb--active' : ''}`}
                      onClick={() => setLightboxIndex(i)}
                    >
                      {isVideo(url) ? (
                        <video src={`${url}#t=0.001`} className="vault-filmstrip-media" muted playsInline preload="metadata" />
                      ) : (
                        <img src={url} alt="" className="vault-filmstrip-media" />
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="vault-modal-actions">
                {selectedContent.mediaUrls.length > 1 && (
                  <span className="vault-asset-counter">
                    {lightboxIndex + 1} / {selectedContent.mediaUrls.length}
                  </span>
                )}
                <button
                  className="download-btn"
                  onClick={() => handleDownload(
                    selectedContent,
                    selectedContent.mediaUrls[lightboxIndex],
                    lightboxIndex,
                    selectedContent.mediaUrls.length
                  )}
                >
                  <Download size={16} /> Download
                </button>
                {selectedContent.mediaUrls.length > 1 && selectedContent.zipKey && (
                  <button
                    className="download-btn"
                    onClick={() => handleDownloadZip(selectedContent)}
                  >
                    <Download size={16} /> Download All
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
